import { FontIcon } from '../FontIcon';
import { LinkProps, LinkWrapper } from '../LinkWrapper';
import { RewardProgress, RewardProgressProps } from '../RewardProgress';
import styles from './rwitem.module.sass';
import cn from 'classnames';
import { Skeleton } from '../Skeleton';

interface CommonProps extends React.HTMLAttributes<HTMLDivElement> {
    heading: string;
    description?: string;
    errorText?: string;
    isLoading?: boolean;
}

type RewardItemProps =
    | (CommonProps & { isLoading: true })
    | (CommonProps & {
          type: 'preview';
          reward: number;
          cta?: LinkProps;
          ctaLinkOnClick?: (e: React.MouseEvent) => void;
          isCompleted?: boolean;
      })
    | (CommonProps & { type: 'inProgress'; progress: RewardProgressProps });

export const RewardItem = ({
    // type,
    heading,
    description,
    errorText,
    isLoading,
    ...props
}: RewardItemProps) => {
    if (isLoading) {
        return (
            <div {...props} className={cn(styles.rwitem, styles.loading, props.className)}>
                <Skeleton absolute />
            </div>
        );
    }

    return (
        <div {...props} className={cn(styles.rwitem, props.className)}>
            <strong className={styles.heading}>{heading}</strong>
            {description && <span className={styles.description}>{description}</span>}
            {errorText && (
                <span className={styles.error}>
                    <FontIcon name="exclamation-mark" size="x-small" md="x-small" color="red" />
                    {errorText}
                </span>
            )}

            {(() => {
                const { type } = props as Extract<RewardItemProps, { type: 'preview' } | { type: 'inProgress' }>;
                switch (type) {
                    case 'preview': {
                        const { reward, cta, isCompleted, ctaLinkOnClick } = props as Extract<
                            RewardItemProps,
                            { type: 'preview' }
                        >;
                        return (
                            <div className={styles.footer}>
                                <div className={styles.reward}>
                                    <FontIcon name="reward" color="gray" />
                                    <span>{reward}</span>
                                </div>
                                {(cta && (
                                    <LinkWrapper
                                        link={cta}
                                        onClick={ctaLinkOnClick}
                                        className={cn(styles.link, isCompleted && styles.finished)}
                                        data-test-id={`reward-${cta.text?.toLowerCase().replace(/\s+/g, '-')}-btn`}
                                    >
                                        {cta.text}
                                        <div className={styles.hexagon}>
                                            <FontIcon name={isCompleted ? 'check-green' : 'arrow-left-thick'} />
                                        </div>
                                    </LinkWrapper>
                                )) ||
                                    null}
                            </div>
                        );
                    }
                    case 'inProgress': {
                        const { progress } = props as Extract<RewardItemProps, { type: 'inProgress' }>;
                        return <RewardProgress className={styles.progress} {...progress} />;
                    }
                    default:
                        return null;
                }
            })()}
        </div>
    );
};
